import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEffect, useState } from "react";
import { z } from "zod";

import { useFeatureFlags } from "@smart/bridge-feature-flags-dom";
import {
  FieldComponentType,
  sectionFallback,
} from "@smart/bridge-intake-components-dom";
import { MeetingType } from "@smart/bridge-types-basic";
import { useStorage } from "@smart/itops-hooks-dom";
import {
  Alert,
  Badge,
  Button,
  ProgressBar,
  ProgressCircle,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";
import {
  DeepPartial,
  getLocalTimeZoneCode,
  isNullOrUndefined,
} from "@smart/itops-utils-basic";
import {
  useDeleteField,
  useDeleteGroup,
  useUpdateGroup,
} from "@smart/manage-gql-client-dom";

import { FeatureInfo } from "./feature-info";
import { Field } from "./field";
import { Group } from "./group";
import { MappedFieldsOptions } from "./mapped-fields-modal";
import { PendingFieldCard } from "./pending-field-card";
import { RemovedFieldCard } from "./removed-field-card";
import { RemovedGroupCard } from "./removed-group-card";
import {
  getDefaultSignatureMethod,
  getSignatureTypeAvailability,
} from "./signature/utils";
import { baseLanguageKey, LanguageKey } from "../../../types";
import { FormLoadingStatus } from "../../create-form/creation-status";
import { UnpublishedChangesIndicator } from "../../shared/unpublished-changes-indicator";
import { DroppableSeparator, EmptyStateDroppable } from "../drag-and-drop";
import { ConditionsButton } from "../edit-view/condition/conditions-button";
import { EditingToolbar, EditableText } from "../editor";
import { useUpdateData } from "../hooks";
import {
  ConditionActions,
  EditableSection,
  FieldTemplate,
  GqlField,
  GqlFieldValues,
  GqlForm,
  GqlGroup,
  GqlGroupWithFieldValues,
  GqlMatterLayout,
  GqlMatterType,
  GqlSectionItem,
  LoadMatterFields,
  QuestionEditMode,
  isSectionItemField,
} from "../types";
import {
  ChangeStatus,
  EntityChangesMap,
  changeStatusLabel,
  getDefaultAvailability,
  isChanged,
} from "../utils";

type QuestionsProps = {
  sectionItems: GqlSectionItem[];
  loading: {
    sections: boolean;
    groups: boolean;
    fields: boolean;
  };
  form?: GqlForm | null;
  section?: EditableSection;
  mode?: QuestionEditMode;
  editingItemUri?: string;
  updatingItems: Record<string, boolean | undefined>;
  mergeUpdateField: ReturnType<typeof useUpdateData>["mergeUpdateField"];
  updateGroup: ReturnType<typeof useUpdateGroup>[0];
  deleteField: ReturnType<typeof useDeleteField>[0];
  deleteGroup: ReturnType<typeof useDeleteGroup>[0];
  updateSection: (
    section: EditableSection,
    values: { title: string; description?: string | null },
  ) => Promise<void>;
  duplicateGroup: ({
    groupToCopyUri,
    newGroupTitle,
    newGroupOrder,
  }: {
    groupToCopyUri: string;
    newGroupTitle?: string;
    newGroupOrder: string;
  }) => Promise<void>;
  onUpdatingFieldDone: () => void;
  matterLayouts?: GqlMatterLayout[];
  matterTypes?: GqlMatterType[];
  loadMatterFields: LoadMatterFields;
  loadingStatus: FormLoadingStatus;
  conditionActions: ConditionActions;
  setSelectedFieldToMap: React.Dispatch<
    React.SetStateAction<GqlFieldValues | undefined>
  >;
  setSignatureFieldUri: (uri: string | undefined) => void;
  checkDigitalSignatureAvailability?: () => Promise<boolean>;
  pendingFieldUris?: string[];
  excludingFieldTemplates: FieldTemplate[];
  lastActiveIds: string[];
  viewingLanguage: LanguageKey;
  hasSubmissionLinks?: boolean;
  fieldChangesMap?: EntityChangesMap;
  groupChangesMap?: EntityChangesMap;
  sectionChangeStatus?: ChangeStatus;
  sectionHasUnpublishedChanges?: boolean;
  isFormEmpty: boolean;
  openContactDetailsModal: () => void;
  openMappedFieldsModal?: (options: MappedFieldsOptions) => void;
  formFields: GqlField[];
  formGroups: GqlGroup[];
};

const Questions = ({
  form,
  section,
  sectionItems,
  loading,
  mode,
  editingItemUri,
  updatingItems,
  mergeUpdateField,
  updateGroup,
  deleteField,
  updateSection,
  onUpdatingFieldDone,
  matterLayouts,
  matterTypes,
  loadMatterFields,
  deleteGroup,
  duplicateGroup,
  loadingStatus,
  conditionActions,
  setSelectedFieldToMap,
  setSignatureFieldUri,
  checkDigitalSignatureAvailability,
  pendingFieldUris,
  excludingFieldTemplates,
  lastActiveIds,
  viewingLanguage,
  hasSubmissionLinks,
  fieldChangesMap,
  groupChangesMap,
  sectionChangeStatus,
  sectionHasUnpublishedChanges,
  isFormEmpty,
  openContactDetailsModal,
  openMappedFieldsModal,
  formFields,
  formGroups,
}: QuestionsProps) => {
  const {
    "LINK-3588": digitalSignatureType,
    "LINK-3846": standardSignatureDisabled,
  } = useFeatureFlags();
  const signatureTypes = getSignatureTypeAvailability({
    digitalSignatureEnabled: digitalSignatureType,
    standardSignatureDisabled,
  });
  const digitalOnlySignature =
    signatureTypes.digital && !signatureTypes.standard;
  const [checkingSignatureFieldUri, setCheckingSignatureFieldUri] =
    useState<string>();
  const displayedUpdatingItems = checkingSignatureFieldUri
    ? { ...updatingItems, [checkingSignatureFieldUri]: true }
    : updatingItems;
  const translatedSection = section?.values?.translations?.find(
    (translation) => translation.language === viewingLanguage,
  );
  const translatedTitle =
    translatedSection?.title || section?.values?.title || sectionFallback;
  const translatedDescription =
    translatedSection?.description || section?.values?.description;
  const isViewingBaseLanguage = viewingLanguage === baseLanguageKey;
  const [editingTitle, setEditingTitle] = useState(translatedTitle);
  const [editedDescription, setEditedDescription] = useState(
    translatedDescription,
  );
  const [descriptionVisible, setDescriptionVisible] = useState(
    !!translatedDescription,
  );
  const [languageWarning, setLanguageWarning] = useStorage({
    defaultValue: "true",
    key: "language-warning-form-builder",
    schema: z.string(),
    storage: "local",
  });

  useEffect(() => {
    setEditingTitle(translatedTitle);
    setEditedDescription(translatedDescription);
    setDescriptionVisible(!!translatedDescription);
  }, [translatedTitle, translatedDescription]);

  const handleSaveTitle = async (value: string) => {
    if (section) {
      setEditingTitle(value);
      await updateSection(section, {
        title: value,
        description: editedDescription,
      });
    }
  };

  const handleSaveDescription = async (value: string) => {
    if (section) {
      setEditedDescription(value);
      await updateSection(section, { title: editingTitle, description: value });
    }
  };

  const onUpdateField = async ({
    field,
    updated,
  }: {
    field: GqlFieldValues;
    updated: DeepPartial<GqlFieldValues>;
  }) => {
    await mergeUpdateField({ current: field, incoming: updated }).catch(
      console.error,
    );
    onUpdatingFieldDone();
  };

  const onDeleteField = async (field: GqlFieldValues) => {
    await deleteField({
      variables: {
        uri: field.uri,
        sectionUri: field.sectionUri,
        formUri: field.formUri,
      },
    }).catch(console.error);
    onUpdatingFieldDone();
  };

  const onFieldTypeChange = async ({
    field,
    newFieldType,
  }: {
    field: GqlFieldValues;
    newFieldType: FieldComponentType;
  }) => {
    if (
      newFieldType === "group" ||
      newFieldType === "contactDetails" ||
      (newFieldType === field.type && !field.layout && !field.field)
    ) {
      return;
    }

    if (newFieldType === "mapped") {
      setSelectedFieldToMap(field);
      return;
    }

    if (newFieldType === "signature" && digitalOnlySignature) {
      setCheckingSignatureFieldUri(field.uri);
      if (!(await checkDigitalSignatureAvailability?.())) {
        setCheckingSignatureFieldUri(undefined);
        return;
      }
    }

    const storedSignatureDefault =
      newFieldType === "signature" &&
      digitalSignatureType &&
      !standardSignatureDisabled &&
      !hasSubmissionLinks
        ? getDefaultSignatureMethod()
        : undefined;
    if (newFieldType === "signature" && !storedSignatureDefault) {
      if (!digitalOnlySignature) setSignatureFieldUri(field.uri);
    }

    const getInitialValues = () => {
      switch (newFieldType) {
        case "appointment":
          return {
            duration: 15,
            availability: getDefaultAvailability(),
            timezone: getLocalTimeZoneCode(),
            meetingType: "inPerson" as MeetingType,
          };
        case "signature":
          return {
            signature: {
              method: digitalOnlySignature
                ? ("digital" as const)
                : (storedSignatureDefault ?? ("electronic" as const)),
            },
          };
        default:
          return undefined;
      }
    };

    await mergeUpdateField({
      current: field,
      incoming: {
        options:
          (newFieldType === "choice" || newFieldType === "checkbox") &&
          field.options
            ? field.options.map((option) => ({
                label: option.label,
                value: option.value,
              }))
            : [],
        supportsCustomValues:
          (newFieldType === "choice" || newFieldType === "checkbox") &&
          !isNullOrUndefined(field.supportsCustomValues)
            ? field.supportsCustomValues
            : undefined,
        type: newFieldType,
        layout: undefined,
        field: undefined,
        links: undefined,
        ...getInitialValues(),
      },
    }).catch(console.error);
    if (newFieldType === "signature" && digitalOnlySignature) {
      setCheckingSignatureFieldUri(undefined);
    }
  };

  const disabled =
    loadingStatus === "checking" ||
    loadingStatus === "timedOut" ||
    form?.aiUserFeedback?.status === "generating" ||
    form?.aiUserFeedback?.status === "generated";

  const isLoadingSectionItems =
    loading.sections || loading.groups || loading.fields;

  const renderSectionItem = (item: GqlSectionItem, index: number) => {
    if (pendingFieldUris?.includes(item.uri) && isSectionItemField(item)) {
      return <PendingFieldCard field={item} />;
    }
    if (fieldChangesMap?.get(item.uri) === "removed") {
      return <RemovedFieldCard field={item as GqlFieldValues} />;
    }
    if (groupChangesMap?.get(item.uri) === "removed") {
      return <RemovedGroupCard group={item as GqlGroupWithFieldValues} />;
    }
    if (isSectionItemField(item)) {
      return (
        <Field
          index={index}
          item={item}
          mode={mode}
          matterTypes={matterTypes}
          onUpdateField={onUpdateField}
          onDeleteField={onDeleteField}
          matterLayouts={matterLayouts}
          loadMatterFields={loadMatterFields}
          loading={displayedUpdatingItems[item.uri]}
          isEditing={item.uri === editingItemUri}
          conditionActions={conditionActions}
          onFieldTypeChange={onFieldTypeChange}
          excludingFieldTemplates={excludingFieldTemplates}
          lastActiveIds={lastActiveIds}
          viewingLanguage={viewingLanguage}
          hasSubmissionLinks={hasSubmissionLinks}
          changeStatus={fieldChangesMap?.get(item.uri)}
        />
      );
    }
    return (
      <Group
        mode={mode}
        index={index}
        item={item}
        fields={item.fields}
        matterTypes={matterTypes}
        updateGroup={updateGroup}
        deleteGroup={deleteGroup}
        duplicateGroup={duplicateGroup}
        sectionItems={sectionItems}
        onUpdateField={onUpdateField}
        onDeleteField={onDeleteField}
        matterLayouts={matterLayouts}
        editingItemUri={editingItemUri}
        loadMatterFields={loadMatterFields}
        updatingItems={displayedUpdatingItems}
        loading={displayedUpdatingItems[item.uri]}
        isEditing={item.uri === editingItemUri}
        conditionActions={conditionActions}
        onFieldTypeChange={onFieldTypeChange}
        excludingFieldTemplates={excludingFieldTemplates}
        lastActiveIds={lastActiveIds}
        viewingLanguage={viewingLanguage}
        hasSubmissionLinks={hasSubmissionLinks}
        fieldChangesMap={fieldChangesMap}
        changeStatus={groupChangesMap?.get(item.uri)}
        openMappedFieldsModal={openMappedFieldsModal}
        formFields={formFields}
        formGroups={formGroups}
      />
    );
  };

  return (
    <div
      data-testid="edit-questions"
      className="flex flex-1 flex-col min-h-0 overflow-hidden bg-neutral-50 pt-0 relative w-full"
    >
      {section && displayedUpdatingItems[section.uri] && (
        <ProgressBar className="h-[0.2rem]" />
      )}
      <div className="flex-1 min-h-0 overflow-auto [&::-webkit-scrollbar-track]:bg-transparent">
        {!disabled && (
          <div className="sticky top-1 z-1 flex justify-center bg-transparent">
            <EditingToolbar />
          </div>
        )}
        <div className="p-8 pb-[10rem]">
          <FeatureInfo
            featureName="publishing-form"
            heading="Publishing is new!"
            content="Your changes save automatically as a new version. Publishing controls when those changes go live."
          />
          {!!form?.rawForm.values.supportedLanguages?.length &&
            languageWarning === "true" && (
              <div className="mx-auto max-w-screen-readable">
                <Alert
                  content="Changes made in English will update all languages. Changes made in another language will only apply to that language."
                  variant="section"
                  onClose={() => {
                    setLanguageWarning("false");
                  }}
                />
              </div>
            )}
          <div
            className="aria-disabled:hidden flex gap-small justify-between items-center max-w-screen-readable mx-auto mt-8 mb-4"
            aria-disabled={disabled}
          >
            {sectionHasUnpublishedChanges && (
              <Tooltip
                text="This section has unpublished changes"
                placement="top"
                offsetBy={7}
                mountTo="body"
              >
                <UnpublishedChangesIndicator dataTestId="section-title-unpublished-indicator" />
              </Tooltip>
            )}
            <div className="flex flex-col items-start justify-center relative flex-1 overflow-hidden">
              <div className="flex items-center gap-small font-poppins w-full">
                <EditableText
                  dataTestId="section-title-edit"
                  text={editingTitle}
                  onChange={setEditingTitle}
                  onComplete={async () => {
                    if (section)
                      await updateSection(section, {
                        title: editingTitle || "",
                        description: editedDescription,
                      });
                  }}
                  onSave={handleSaveTitle}
                  disabled={disabled}
                  loading={loading.sections}
                  fontSize="h3"
                  fontWeight="semibold"
                />
                {isChanged(sectionChangeStatus) && (
                  <div>
                    <Badge
                      label={changeStatusLabel[sectionChangeStatus]}
                      theme="information"
                    />
                  </div>
                )}
              </div>
              {descriptionVisible && (
                <EditableText
                  dataTestId="section-description-edit"
                  text={editedDescription}
                  onChange={setEditedDescription}
                  onSave={handleSaveDescription}
                  onComplete={async () => {
                    if (section)
                      await updateSection(section, {
                        title: editingTitle,
                        description: editedDescription || "",
                      });
                  }}
                  placeholder="Description"
                  isEditor
                />
              )}
            </div>
            {section && (
              <div className="flex gap-small h-full justify-end items-start flex-wrap w-[10rem] pr-[4px]">
                <Tooltip
                  title="Description text"
                  text="Add a description to guide clients on what to enter."
                  placement="top-end"
                  offsetBy={7}
                  variant="paragraphy"
                >
                  <Button
                    variant="secondarySubtle"
                    leftIcon="regularText"
                    onClick={async () => {
                      setDescriptionVisible(!descriptionVisible);
                      if (descriptionVisible) {
                        setEditedDescription("");
                        if (section) {
                          await updateSection(section, {
                            title: editingTitle,
                            description: "",
                          });
                        }
                      }
                    }}
                    size="small"
                    isPressed={descriptionVisible}
                    disabled={!isViewingBaseLanguage}
                    dataTestId="toggle-section-description"
                  />
                </Tooltip>
                {conditionActions.checkHasConditions(section.uri) && (
                  <ConditionsButton
                    numberOfConditions={conditionActions.getNumberOfConditions(
                      section.uri,
                    )}
                    onClick={() =>
                      section.values &&
                      conditionActions.setEditingCondition(section.values)
                    }
                    dataTestId="section-conditions-button"
                    size="small"
                  />
                )}
              </div>
            )}
          </div>
          <div
            aria-disabled={disabled}
            className="flex flex-col aria-disabled:pointer-events-none aria-disabled:opacity-50"
          >
            <SortableContext
              items={sectionItems.map((i) => i.uri)}
              strategy={verticalListSortingStrategy}
            >
              {sectionItems.length > 0 && (
                <DroppableSeparator
                  index={0}
                  disabled={mode === "reordering"}
                />
              )}
              {!sectionItems.length && !isLoadingSectionItems && (
                <EmptyStateDroppable
                  index={0}
                  disabled={mode === "reordering"}
                  variant={isFormEmpty ? "form" : "section"}
                  openContactDetailsModal={openContactDetailsModal}
                />
              )}
              {!sectionItems.length && isLoadingSectionItems && (
                <ProgressCircle className="my-8 mx-auto" size="medium" />
              )}
              {sectionItems.map((item, index) => (
                <div key={item.uri} data-testid="section-item-container">
                  {renderSectionItem(item, index)}
                </div>
              ))}
            </SortableContext>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Questions };
